// File: src/client/components/conversation/ConversationView.js
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    clearError,
    clearPersonasError,
    fetchAvailablePersonas,
    resetConversation,
    selectIsConversationExpanded,
    sendMessage,
    sendMessageStreaming,
    setForceWebSearch,
    setSelectedPersona,
    resumeConversation,
    toggleConversationExpanded,
    updateUserInput,
} from '../../slices/conversationSlice.mjs';

const MaximizeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-5 h-5"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m4.5 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
        />
    </svg>
);

const MinimizeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-5 h-5"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25"
        />
    </svg>
);

const handleInitialExpand = () => {
    setIsInitialCollapsed(false);
};

const LARGE_BREAKPOINT_QUERY = '(min-width: 1024px)'; // Use a constant for clarity
const DEFAULT_FIXED_TOP_OFFSET = 65; // This is for the non-expanded mobile sticky behavior

const ConversationView = ({
    user,
    articleId,
    fixedTopOffset = DEFAULT_FIXED_TOP_OFFSET,
    summaryHeight = null,
    versionContext = null,
}) => {
    const dispatch = useDispatch();
    const isConversationGloballyExpanded = useSelector(selectIsConversationExpanded);
    const forceWebSearch = useSelector((state) => state.conversationSlice.forceWebSearch);
    // ADD THIS STATE to track if user manually selected a persona
    const [userHasManuallySelected, setUserHasManuallySelected] = useState(false);

    const {
        messages,
        currentUserInput,
        isLoading: isLoadingConversation,
        error: conversationError,
        currentInternalConversationId,
        availablePersonas,
        selectedPersona,
        isLoadingPersonas,
        personasError,
    } = useSelector((state) => state.conversationSlice);

    // Inline persona matching (replaces deleted conversationSelectors.mjs)
    const currentArticle = useSelector(
        (state) => state.unifiedArticles?.currentArticle || state.siteArticles?.currentArticle
    );
    const matchingPersonaForArticle = React.useMemo(() => {
        if (!currentArticle?.authorPersona?.name || !Array.isArray(availablePersonas) || availablePersonas.length === 0) {
            return null;
        }
        return availablePersonas.find((p) => p.name === currentArticle.authorPersona.name) || null;
    }, [currentArticle, availablePersonas]);
    const firstAvailablePersona = availablePersonas.length > 0 ? availablePersonas[0] : null;

    const [isConvoFixed, setIsConvoFixed] = useState(false);
    const [convoWidth, setConvoWidth] = useState('auto');
    const [headerHeight, setHeaderHeight] = useState(0);
    const [articleDetailTopPadding, setArticleDetailTopPadding] = useState(0); // Add this state
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    const [isInitialCollapsed, setIsInitialCollapsed] = useState(true);
    const [componentHeight, setComponentHeight] = useState(0);

    // Auto-scroll control states
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Set back to true
    const [justSentMessage, setJustSentMessage] = useState(false);

    const convoRef = useRef(null);
    const initialConvoTopRef = useRef(null);
    const conversationFetchAttemptedForPersonaRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    console.log(
        'ConversationView render - messages count:',
        messages.length,
        'streaming messages:',
        messages.filter((msg) => msg.isStreaming).length
    );

    useEffect(() => {
        // When on medium or larger screens, don't use the collapsed state
        if (isLargeScreen || window.matchMedia('(min-width: 768px)').matches) {
            setIsInitialCollapsed(false);
        }
    }, [isLargeScreen]);

    // --- Effect to calculate header height, article detail padding & track screen size ---
    useEffect(() => {
        // This code ONLY runs on the client
        const headerElement = document.querySelector('.app-header');
        const articleDetailElement = document.querySelector('.article-detail-container');

        const updateHeightAndPadding = () => {
            if (headerElement) {
                setHeaderHeight(headerElement.offsetHeight);
            }

            if (articleDetailElement) {
                const computedStyle = window.getComputedStyle(articleDetailElement);
                const topPadding = parseInt(computedStyle.paddingTop, 10) || 0;
                setArticleDetailTopPadding(topPadding);
            }
        };

        // Check initial screen size ON CLIENT mount
        const mediaQueryList = window.matchMedia(LARGE_BREAKPOINT_QUERY);
        setIsLargeScreen(mediaQueryList.matches); // Set initial value client-side

        const handleResize = () => {
            updateHeightAndPadding();
            setIsLargeScreen(mediaQueryList.matches); // Update on resize
        };

        updateHeightAndPadding(); // Initial calculation
        mediaQueryList.addEventListener('change', handleResize);
        window.addEventListener('resize', updateHeightAndPadding); // Update on resize

        return () => {
            mediaQueryList.removeEventListener('change', handleResize);
            window.removeEventListener('resize', updateHeightAndPadding);
        };
    }, []); // Empty dependency array ensures this runs once on mount

    // --- Effect for Scroll Listener (only when not expanded *and* not large screen) ---
    useEffect(() => {
        if (isConversationGloballyExpanded || isLargeScreen) {
            setIsConvoFixed(false);
            setConvoWidth('auto');
            return;
        }

        const convoElement = convoRef.current;
        if (!convoElement) return;

        // Add this function to update the component height
        const updateComponentHeight = () => {
            setComponentHeight(convoElement.offsetHeight);
        };

        // Initial calculation
        updateComponentHeight();

        const calculateInitialPosition = () => {
            if (initialConvoTopRef.current === null || !isConvoFixed) {
                const rect = convoElement.getBoundingClientRect();
                initialConvoTopRef.current = rect.top + window.scrollY;
            }
        };
        requestAnimationFrame(calculateInitialPosition);

        const handleScroll = () => {
            if (isConversationGloballyExpanded || isLargeScreen) return; // Exit if expanded or on large screen

            // Mobile/Tablet logic (only runs if !isExpanded and !isLargeScreen)
            if (initialConvoTopRef.current === null) {
                calculateInitialPosition();
                if (initialConvoTopRef.current === null) return;
            }

            const scrollY = window.scrollY;

            // Get the chat component height - consider if it's in collapsed state
            const chatHeight = isInitialCollapsed
                ? Math.min(convoElement.offsetHeight, 60) // Use 60px as collapsed height
                : convoElement.offsetHeight;

            // Calculate new threshold to ensure component is fully under header
            // Only fix position after the BOTTOM of the component would go under the header
            const threshold = initialConvoTopRef.current + chatHeight - (headerHeight + 10); // +10px buffer

            if (scrollY >= threshold && !isConvoFixed) {
                // Update height before fixing
                updateComponentHeight();
                setConvoWidth(`${convoElement.offsetWidth}px`);
                setIsConvoFixed(true);
            } else if (scrollY < threshold && isConvoFixed) {
                setIsConvoFixed(false);
                setConvoWidth('auto');
            }
        };

        // Add resize handler
        const handleResize = () => {
            if (isConversationGloballyExpanded || isLargeScreen) return;
            initialConvoTopRef.current = null;
            if (convoRef.current && !isConvoFixed) {
                setConvoWidth('auto');
            }
            updateComponentHeight(); // Update height on resize
            handleScroll();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [
        isConvoFixed,
        fixedTopOffset,
        isConversationGloballyExpanded,
        isLargeScreen,
        isInitialCollapsed,
    ]);

    // Also add an update whenever initial collapsed state changes
    useEffect(() => {
        if (convoRef.current) {
            setComponentHeight(convoRef.current.offsetHeight);
        }
    }, [isInitialCollapsed]);

    // --- Effect for Persona Fetching and Resuming Conversation ---
    useEffect(() => {
        // 1. Fetch personas if we don't have them
        if (
            user &&
            (!Array.isArray(availablePersonas) || availablePersonas.length === 0) &&
            !isLoadingPersonas &&
            !personasError
        ) {
            dispatch(fetchAvailablePersonas());
        }

        // 2. Auto-select a persona if the user hasn't chosen one
        if (availablePersonas.length > 0 && !userHasManuallySelected) {
            const personaToSelect =
                matchingPersonaForArticle || selectedPersona || firstAvailablePersona;
            if (
                personaToSelect &&
                (!selectedPersona || selectedPersona._id !== personaToSelect._id)
            ) {
                dispatch(setSelectedPersona(personaToSelect));
            }
        }

        // 3. Attempt to resume an existing conversation when a persona is selected
        if (user && articleId && selectedPersona?._id) {
            // If the current persona is different from the one we last tried to fetch for, reset the attempt flag.
            if (conversationFetchAttemptedForPersonaRef.current !== selectedPersona._id) {
                conversationFetchAttemptedForPersonaRef.current = null;
            }

            // Only try to fetch if we haven't already for this persona.
            // The `setSelectedPersona` reducer clears `currentInternalConversationId`, which allows this to run.
            if (
                !currentInternalConversationId &&
                !isLoadingConversation &&
                !conversationError &&
                !conversationFetchAttemptedForPersonaRef.current
            ) {
                console.log('Dispatching resumeConversation for persona:', selectedPersona.name);
                conversationFetchAttemptedForPersonaRef.current = selectedPersona._id; // Mark that we are attempting for this persona

                dispatch(resumeConversation({ articleId, personaId: selectedPersona._id }))
                    .unwrap()
                    .catch((err) => {
                        console.error('ConversationView: Failed to resume conversation:', err);
                        // Allow another attempt if the fetch failed
                        conversationFetchAttemptedForPersonaRef.current = null;
                    });
            }
        }
    }, [
        user,
        articleId,
        availablePersonas,
        isLoadingPersonas,
        personasError,
        selectedPersona,
        currentInternalConversationId,
        isLoadingConversation,
        conversationError,
        matchingPersonaForArticle,
        firstAvailablePersona,
        userHasManuallySelected,
        dispatch,
    ]);

    // Modified auto-scroll effect - stops when current response is at top of container
    useEffect(() => {
        if (!autoScrollEnabled) return;

        const hasStreamingMessage = messages.some((msg) => msg.isStreaming);
        const container =
            messagesContainerRef.current ||
            messagesEndRef.current?.closest('.article-conversation-messages');

        if (!container) return;

        if (hasStreamingMessage) {
            // Find the current streaming message element
            const messageElements = container.querySelectorAll('.message');
            const streamingElement = Array.from(messageElements).find((el) =>
                el.classList.contains('streaming-message')
            );

            if (streamingElement) {
                const containerScrollTop = container.scrollTop;
                const elementOffsetTop = streamingElement.offsetTop;

                // Only scroll if the streaming message is not at the top of the container
                // Subtract a small buffer so it doesn't scroll quite as far
                if (elementOffsetTop > containerScrollTop) {
                    container.scrollTop = elementOffsetTop - 200; // Subtract 10px buffer
                }
            }
        }
        // Remove the final scroll to bottom when streaming completes
    }, [messages, autoScrollEnabled]);

    // NEW: Scroll messages container to bottom instantly after sending message
    useEffect(() => {
        if (justSentMessage) {
            const container =
                messagesContainerRef.current ||
                messagesEndRef.current?.closest('.article-conversation-messages');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
            setJustSentMessage(false);
        }
    }, [justSentMessage]);

    // NEW: Scroll to bottom when past conversation is loaded
    useEffect(() => {
        if (currentInternalConversationId && messages.length > 1) {
            const container =
                messagesContainerRef.current ||
                messagesEndRef.current?.closest('.article-conversation-messages');
            // Check if this is a loaded conversation (not streaming)
            const hasStreamingMessage = messages.some((msg) => msg.isStreaming);
            if (!hasStreamingMessage && container) {
                container.scrollTop = container.scrollHeight;
            }
        }
    }, [currentInternalConversationId, messages.length]);

    // Add this new useEffect after the existing ones:
    useEffect(() => {
        // Force re-render for streaming messages
        const streamingMessage = messages.find((msg) => msg.isStreaming);
        if (streamingMessage) {
            console.log(
                'Streaming message updated:',
                streamingMessage.content.length,
                'characters'
            );
        }
    }, [messages.map((msg) => (msg.isStreaming ? msg.content : msg._id)).join('|')]);

    const handleSendMessage = () => {
        if (!user || !currentUserInput.trim() || isLoadingConversation) {
            return;
        }

        // A persona must be selected to send a message.
        if (!selectedPersona?._id) {
            console.error('Cannot send message: No persona selected.');
            // Optionally, provide user feedback here (e.g., a toast notification).
            return;
        }

        setJustSentMessage(true);
        dispatch(
            sendMessageStreaming({
                userMessage: currentUserInput,
                // Pass article and persona IDs only if we are starting a new conversation.
                articleId: currentInternalConversationId ? undefined : articleId,
                personaId: currentInternalConversationId ? undefined : selectedPersona._id,
                // Include version context if available and starting new conversation
                versionContext: currentInternalConversationId ? undefined : versionContext,
            })
        );
    };

    const handlePersonaChange = (event) => {
        const selectedPersonaName = event.target.value;

        // Mark that user has manually selected a persona
        setUserHasManuallySelected(true);

        if (!selectedPersonaName) {
            dispatch(setSelectedPersona(null));
            return;
        }

        const foundPersona = availablePersonas.find((p) => p.name === selectedPersonaName);

        if (foundPersona) {
            dispatch(setSelectedPersona(foundPersona));
        }
    };

    const handleToggleExpand = () => {
        // Renamed for clarity, dispatches Redux action
        dispatch(toggleConversationExpanded());
        // Reset mobile sticky state if collapsing (isConversationGloballyExpanded will be true before this dispatch flips it for next render)
        if (isConversationGloballyExpanded) {
            setIsConvoFixed(false);
            setConvoWidth('auto');
            initialConvoTopRef.current = null;
        }
    };

    // Add these state variables to track dragging
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [isPeeking, setIsPeeking] = useState(false);
    const dragStartY = useRef(0);
    const dragHandleRef = useRef(null);

    // Add touch event handlers
    const handleTouchStart = (e) => {
        if (!isConversationGloballyExpanded || isLargeScreen) return;
        dragStartY.current = e.touches[0].clientY;
        setIsDragging(true);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;

        const currentY = e.touches[0].clientY;
        const newOffset = Math.max(0, currentY - dragStartY.current);

        // Limit maximum drag to 50% of the viewport height
        const maxDrag = window.innerHeight * 0.5;
        setDragOffset(Math.min(newOffset, maxDrag));
    };

    const handleTouchEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);

        // If dragged more than 20% of the screen height, snap to peek mode
        // otherwise snap back to full expanded mode
        const peekThreshold = window.innerHeight * 0.2;

        if (dragOffset > peekThreshold) {
            setIsPeeking(true);
            setDragOffset(peekThreshold); // Snap to peek height
        } else {
            setIsPeeking(false);
            setDragOffset(0); // Snap back to full height
        }
    };

    // Update the getExpandedMobileStyle function to also handle messages area height
    const getExpandedMobileStyle = () => {
        if (!isConversationGloballyExpanded || isLargeScreen) return {};

        const containerHeight = `calc(100vh - ${headerHeight}px - ${dragOffset}px)`;

        return {
            transform: `translateY(${dragOffset}px)`,
            height: isDragging || isPeeking ? containerHeight : `calc(100vh - ${headerHeight}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease, height 0.3s ease',
        };
    };

    // Replace the getMessagesAreaStyle function with this simple one:
    const getMessagesAreaStyle = () => {
        if (!isConversationGloballyExpanded || isLargeScreen) return {};

        // Just subtract the drag distance from the height, that's it
        return {
            maxHeight: `calc(30vh - ${dragOffset}px)`,
            height: `calc(25vh - ${dragOffset}px)`,
            minHeight: '75px',
        };
    };

    // Determine dynamic styles for the container
    useEffect(() => {
        // When summaryHeight changes, log it for debugging
        if (summaryHeight && isLargeScreen && !isConversationGloballyExpanded) {
            console.log('Applying summary height:', summaryHeight);
        }
    }, [summaryHeight, isLargeScreen, isConversationGloballyExpanded]);

    const containerStyle = {
        '--header-height': `${headerHeight}px`, // Pass header height as CSS variable
        '--header-height-with-padding': `${headerHeight + articleDetailTopPadding}px`, // Include computed padding
        '--sticky-top-offset-mobile': `${fixedTopOffset}px`, // Pass mobile offset as CSS variable
        // Add a max-height for the message container when in expanded mobile view
        '--max-messages-height-mobile':
            isConversationGloballyExpanded && !isLargeScreen ? 'calc(70vh - 180px)' : 'auto', // Reserve space for input and header
        ...(isConversationGloballyExpanded && !isLargeScreen ? getExpandedMobileStyle() : {}),
    };

    // Apply summary height matching for desktop/tablet when not expanded
    if (isLargeScreen && !isConversationGloballyExpanded && summaryHeight && summaryHeight > 0) {
        // Force the height to exactly match the summary
        containerStyle.height = `max(${summaryHeight}px, 500px)`;
        // Ensure box-sizing is border-box
        containerStyle.boxSizing = 'border-box';
        // Enable scrolling within the fixed height container
        containerStyle.overflow = 'auto';
        // Use flex layout to distribute space
        containerStyle.display = 'flex';
        containerStyle.flexDirection = 'column';
    }
    let containerClassName = 'article-conversation-container';

    if (isConversationGloballyExpanded) {
        // Apply different classes based on screen size for expanded state
        if (isLargeScreen) {
            containerClassName += ' lg-expanded'; // Special class for large screen expanded view
        } else {
            containerClassName += ' expanded'; // Full overlay class for smaller screens
            if (isDragging) {
                containerClassName += ' dragging'; // Add dragging state
            }
            // Apply top offset directly for mobile fixed overlay
            containerStyle.top = `var(--header-height)`;
        }
    } else {
        // Handle non-expanded states
        if (!isLargeScreen && isConvoFixed) {
            containerClassName += ' is-fixed'; // Mobile sticky class
            containerStyle.width = convoWidth;
            containerStyle.top = `var(--sticky-top-offset-mobile)`;
        }
        // On large screens, the default sticky behavior is handled by CSS `lg:sticky`
        // Add a class if needed for default lg sticky top:
        // containerClassName += " lg-sticky-default";
    }

    // Reset peek state when closing the expanded view
    useEffect(() => {
        if (!isConversationGloballyExpanded) {
            setIsPeeking(false);
            setDragOffset(0);
        }
    }, [isConversationGloballyExpanded]);

    return (
        <>
            <div
                ref={convoRef}
                className={`${containerClassName} ${isInitialCollapsed && !isLargeScreen && !isConversationGloballyExpanded ? 'initial-collapsed' : ''}`}
                style={containerStyle}
            >
                {isConversationGloballyExpanded && !isLargeScreen && (
                    <div
                        className="drag-handle-container"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="drag-handle" aria-label="Pull down to peek at article">
                            <div className="drag-handle-pill"></div>
                            {isPeeking && (
                                <div className="drag-handle-text">
                                    Release to return to full view
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="article-conversation-header">
                    <h3 className="article-conversation-title">Chat</h3>
                    <button
                        onClick={handleToggleExpand}
                        className="expand-collapse-button"
                        aria-label={
                            isConversationGloballyExpanded ? 'Collapse chat' : 'Expand chat'
                        }
                    >
                        {isConversationGloballyExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
                    </button>
                </div>

                {isInitialCollapsed && !isLargeScreen && !isConversationGloballyExpanded ? (
                    <div
                        className="article-conversation-collapsed-prompt cursor-pointer"
                        onClick={handleInitialExpand}
                    >
                        <p className="text-xs text-theme-text-muted">
                            Tap to chat about this article <span className="text-theme-primary">→</span>
                        </p>
                    </div>
                ) : (
                    <div
                        className={`article-conversation-content-wrapper ${isConversationGloballyExpanded ? (isLargeScreen ? 'lg-expanded' : 'expanded') : ''}`}
                    >
                        <div className="persona-selector-area">
                            {isLoadingPersonas && (
                                <p className="text-sm text-theme-text-muted italic">
                                    Loading personas...
                                </p>
                            )}
                            {personasError && (
                                <div className="text-theme-error text-sm p-2 border border-theme-error/30 bg-theme-error/10 rounded">
                                    Error loading personas: {personasError}
                                    <button
                                        onClick={() => dispatch(clearPersonasError())}
                                        className="ml-2 text-xs underline"
                                    >
                                        (Clear)
                                    </button>
                                </div>
                            )}
                            {!isLoadingPersonas &&
                                !personasError &&
                                Array.isArray(availablePersonas) &&
                                availablePersonas.length > 0 && (
                                    <>
                                        <label
                                            htmlFor="persona-select"
                                            className="block text-sm font-medium text-theme-text mb-1"
                                        >
                                            Select Persona:
                                        </label>
                                        <select
                                            id="persona-select"
                                            value={selectedPersona?.name || ''}
                                            onChange={handlePersonaChange}
                                            className="w-full p-2 border border-theme-border bg-theme-surface rounded-md shadow-sm focus:outline-none focus:ring-theme-primary focus:border-theme-primary text-sm text-theme-text"
                                            disabled={
                                                availablePersonas.length === 0 ||
                                                isLoadingConversation
                                            }
                                        >
                                            <option value="">-- Select a Persona --</option>
                                            {availablePersonas.map(
                                                (persona) =>
                                                    persona &&
                                                    persona._id &&
                                                    persona.name && (
                                                        <option
                                                            key={persona._id}
                                                            value={persona.name}
                                                        >
                                                            {persona.name}{persona.primary_sport ? ` — ${persona.primary_sport}` : ''}
                                                        </option>
                                                    )
                                            )}
                                        </select>
                                    </>
                                )}
                            {!isLoadingPersonas &&
                                !personasError &&
                                (!Array.isArray(availablePersonas) ||
                                    availablePersonas.length === 0) && (
                                    <p className="text-sm text-theme-text-muted italic">
                                        No personas available.
                                    </p>
                                )}
                            {/* Add toggle switch */}
                            <div className="web-search-toggle mt-2">
                                <label
                                    htmlFor="force-web-search"
                                    className="block text-sm font-medium text-theme-text mb-1"
                                >
                                    Force Web Search
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                    <input
                                        type="checkbox"
                                        id="force-web-search"
                                        checked={forceWebSearch}
                                        onChange={(e) =>
                                            dispatch(setForceWebSearch(e.target.checked))
                                        }
                                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-theme-surface border-4 appearance-none cursor-pointer"
                                    />
                                    <label
                                        htmlFor="force-web-search"
                                        className={`toggle-label block overflow-hidden h-6 rounded-full bg-theme-surface-muted cursor-pointer ${forceWebSearch ? 'bg-theme-success' : ''}`}
                                    ></label>
                                </div>
                            </div>

                            {/* Version Context Information */}
                            {versionContext && versionContext.hasVersions && (
                                <div className="version-context-info mt-2 p-2 bg-theme-info/10 rounded text-xs">
                                    {versionContext.isViewingVersion ? (
                                        <span className="text-theme-info">
                                            💬 Discussing Version {versionContext.versionNumber}
                                            <span className="ml-1 text-theme-info/80">
                                                (ID: {versionContext.currentVersionId?.slice(-8)})
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-theme-text-muted">
                                            💬 Discussing Original Article
                                            <span className="ml-1 opacity-70">
                                                (
                                                {versionContext.hasVersions
                                                    ? `${versionContext.selectedVersionIndex + 1} versions available`
                                                    : 'No versions'}
                                                )
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div
                            ref={messagesContainerRef}
                            className="article-conversation-messages"
                            style={getMessagesAreaStyle()}
                        >
                            {!selectedPersona?._id && (
                                <p className="article-conversation-messages-placeholder text-sm text-theme-text-muted italic">
                                    Please select a persona to begin.
                                </p>
                            )}
                            {selectedPersona?._id &&
                                isLoadingConversation &&
                                messages.length === 0 && (
                                    <p className="article-conversation-messages-placeholder text-sm text-theme-text-muted italic">
                                        Checking for past messages...
                                    </p>
                                )}
                            {selectedPersona?._id &&
                                !isLoadingConversation &&
                                !currentInternalConversationId &&
                                messages.length === 0 && (
                                    <p className="article-conversation-messages-placeholder">
                                        No messages yet. Type below to start a new conversation!
                                    </p>
                                )}
                            {messages.map((msg, index) => (
                                <div
                                    key={msg._id || `${index}-${msg.role}-${msg.timestamp}`}
                                    className={`message mb-2 p-2 rounded text-sm clear-both ${msg.role === 'user'
                                        ? 'bg-theme-primary/10 text-theme-text float-right'
                                        : msg.role === 'assistant'
                                            ? 'bg-theme-surface-muted text-theme-text float-left'
                                            : 'bg-theme-warning/20 text-theme-text text-center'
                                        } ${msg.isStreaming ? 'streaming-message' : ''}`}
                                >
                                    <p className="whitespace-pre-wrap">
                                        {msg.content}
                                        {msg.isStreaming && (
                                            <span className="streaming-cursor animate-pulse">
                                                ▊
                                            </span>
                                        )}
                                    </p>
                                    {msg.isStreaming && (
                                        <div className="text-xs text-theme-text-muted mt-1 italic">
                                            {selectedPersona?.name || 'Assistant'} is typing...
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div style={{ clear: 'both' }}></div>
                            {isLoadingConversation && !messages.some((msg) => msg.isStreaming) && (
                                <div className="message float-left text-sm text-theme-text-muted italic p-2">
                                    {selectedPersona?.name || 'Assistant'} is thinking...
                                </div>
                            )}
                            <div style={{ clear: 'both' }}></div>
                            {/* Auto-scroll anchor */}
                            <div ref={messagesEndRef} />
                        </div>

                        {conversationError && (
                            <div className="conversation-error-display text-theme-error text-sm p-2 border border-theme-error/30 bg-theme-error/10 rounded">
                                Error: {conversationError}
                                <button
                                    onClick={() => dispatch(clearError())}
                                    className="ml-2 text-xs underline"
                                >
                                    (Clear)
                                </button>
                            </div>
                        )}

                        <div className="article-conversation-input-area">
                            <textarea
                                placeholder={
                                    !selectedPersona
                                        ? 'Select a persona to begin...'
                                        : 'Type your message...'
                                }
                                rows="3"
                                className="article-conversation-textarea"
                                value={currentUserInput}
                                onChange={(e) => dispatch(updateUserInput(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                disabled={
                                    isLoadingConversation || isLoadingPersonas || !selectedPersona
                                }
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={
                                    isLoadingConversation ||
                                    !currentUserInput.trim() ||
                                    !selectedPersona
                                }
                                className="article-conversation-send-button"
                            >
                                {isLoadingConversation ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                )}
            </div >

            {/* Placeholder div - only visible when conversation is fixed */}
            {
                isConvoFixed && !isLargeScreen && !isConversationGloballyExpanded && (
                    <div
                        className="conversation-placeholder w-full"
                        style={{ height: `${componentHeight}px` }}
                        aria-hidden="true"
                    />
                )
            }
        </>
    );
};

export default ConversationView;
